import { EVERY_ROW, ExpressionNode, PageId, QueryFilter, QueryRequest, ViewType } from '@sdk';
import { useEntitiesQuery } from '@sdk/react/hooks';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { iconForType, labelForType } from '@src/components/graph-view/icons/iconRegistry';
import { hubEntityDock } from '@src/lib/hub-page-url';
import { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';

/**
 * HubRecordsView — a generic entity list for the hub page. Lists one OSS entity
 * type via the hub's `graph/<type>` **read** (access-scoped; the hub owns the
 * API/security), rendered with OSS UI primitives. No hub-specific ontology.
 *
 * Reuse-first (see [[oss-ontology-hub-api-division]]): rows open the existing OSS
 * viewer for that type under page=hub. Phase 2a wires the pure-graph types only
 * (Conversations); content-bearing types (docs/tasks) are deferred until the
 * OSS content model reads from entity fields.
 *
 * URL: /dock/hub/records/<type>
 */

// Minimal shape we read off any listed entity for a row (`displayName` is the
// SDK's canonical label getter, present on every hydrated entity).
type Row = { id?: string; displayName?: string };

/**
 * Headings for the types the RAIL points at — the rail's slot name, not the type's
 * name, so the page reads as the icon the user clicked.
 *
 * These deliberately differ from the registry's `display_name`, which is the word
 * for the *type* ("Documents" for markdown, "Task" for task) and is mirrored from
 * flow_sdk so desk and hub agree. A rail slot is free to be called something else
 * — "Stream Inbox" is not what a `conversation` is — so this cannot be sourced from
 * `labelForType`. It can't be sourced from the rail either: those titles come from
 * a lingui macro tag, which returns an empty string once passed across a module
 * boundary. Anything not listed falls back to the registry word.
 */
const RAIL_HEADING: Record<string, string> = {
  conversation: 'Stream Inbox',
  task: 'Tasks',
  markdown: 'Docs',
};

/** How many records a page of the list asks for. */
export const HUB_RECORDS_PAGE_SIZE = 100;

export function HubRecordsView({ type }: { type?: string }) {
  const { navigation } = useDockNavigation();

  // A conversation its owner deleted stays readable to that owner on the hub (``deleted_at``
  // set, kept for audit) — it is gone from the Stream Inbox, so the list asks for live rows only.
  const request = useMemo(
    () =>
      type
        ? new QueryRequest({
            type,
            // The newest page; the server says when more are behind it (`loadMore`).
            query: new QueryFilter({
              match:
                type === 'conversation' ? { op: '$IS_NULL', operands: ['deleted_at'] } : new ExpressionNode(EVERY_ROW),
              limit: HUB_RECORDS_PAGE_SIZE,
              order_by: [{ created_date: 'desc' }, { id: 'asc' }],
            }),
            scope: [],
            name: `hub-records-${type}`,
          })
        : null,
    [type],
  );
  // Generic list — the concrete entity type is data-driven; we only read `id`
  // and the SDK `displayName` label off each hydrated entity.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, isLoading, hasMore, loadMore, isLoadingMore } = useEntitiesQuery<any>(request as QueryRequest, {
    enabled: !!type && !!request,
  });
  const rows = (data ?? []) as Row[];

  const open = (row: Row) => {
    if (!row.id || !type) return;
    // Conversations reuse the OSS ConversationRoute (pure-graph). Every other
    // type opens in the generic HubEntityView (title + entity-field content).
    if (type === 'conversation') {
      navigation.openPage(PageId.HUB, ViewType.CONVERSATION, row.id);
    } else if (type === 'organization') {
      // The Organization screen renders an org; the generic entity view only shows "No content.".
      navigation.openPage(PageId.HUB, ViewType.ORGANIZATION, `organization/${row.id}`);
    } else {
      navigation.openDock(hubEntityDock(type, row.id));
    }
  };

  // `labelForType` (registry `display_name`, else the generic title-caser) rather
  // than the raw type string, so a type with no rail slot still reads as a word.
  const label = (type && (RAIL_HEADING[type] || labelForType(type))) || 'Records';
  const RowIcon = iconForType(type ?? '');

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-4 px-4 py-8">
        <h1 className="text-xl font-semibold">{label}</h1>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">
            <Trans>Loading…</Trans>
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            <Trans>Nothing here yet.</Trans>
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => open(row)}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-start transition-colors hover:bg-accent"
              >
                <RowIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-sm">{row.displayName || row.id}</span>
              </button>
            ))}
            {hasMore && (
              <button
                type="button"
                disabled={isLoadingMore}
                onClick={() => void loadMore?.()}
                data-testid="hub-records-load-more"
                className="rounded-lg border border-dashed border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent disabled:opacity-60"
              >
                {isLoadingMore ? <Trans>Loading…</Trans> : <Trans>Load more</Trans>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HubRecordsView;
